import type { EventTicketUrlKind } from '@pulsify/shared/dto'
import { useRef } from 'react'
import { guessTicketUrlKind } from '~/entities/event/lib/ticket-url-kind'
import * as m from '~/paraglide/messages'
import { normalizeUrl } from '~/shared/lib/form-utils'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'

export function TicketLinkField({
  form,
  label,
  required = false,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
  label?: string
  required?: boolean
}) {
  const initialUrl = useRef<string>(form.getFieldValue('ticketUrl') ?? '')
  const picked = useRef(false)

  const guessKind = (url: string) => {
    if (picked.current || url === initialUrl.current || !url.trim()) return
    form.setFieldValue('ticketUrlKind', guessTicketUrlKind(url))
  }

  return (
    <form.Field
      name="ticketUrl"
      validators={
        required
          ? {
              onSubmit: ({ value }: { value: string }) =>
                value?.trim() ? undefined : m.entryLinkMissing(),
            }
          : undefined
      }
    >
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => (
        <div className="space-y-2" data-field="ticketUrl">
          <Label htmlFor={field.name}>
            {label ?? (required ? m.entryLinkLabel() : m.ticketLinkLabel())}
          </Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id={field.name}
              name={field.name}
              type="url"
              className="sm:flex-1"
              value={field.state.value ?? ''}
              onBlur={() => {
                const url = normalizeUrl(field.state.value ?? '')
                field.handleChange(url)
                field.handleBlur()
                guessKind(url)
              }}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder={m.httpsticketsexamplecom()}
            />
            {field.state.value?.trim() ? (
              <form.Field name="ticketUrlKind">
                {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
                {(kind: any) => (
                  <SegmentedControl<EventTicketUrlKind>
                    label={m.ticketLinkKindLabel()}
                    value={kind.state.value ?? 'tickets'}
                    options={[
                      { value: 'tickets', label: m.ticketLinkKindTickets() },
                      { value: 'registration', label: m.ticketLinkKindSignup() },
                    ]}
                    onChange={(value) => {
                      picked.current = true
                      kind.handleChange(value)
                    }}
                  />
                )}
              </form.Field>
            ) : null}
          </div>
          {field.state.value?.trim() ? (
            <form.Subscribe
              selector={(state: { values: { ticketUrlKind?: EventTicketUrlKind } }) =>
                state.values.ticketUrlKind
              }
            >
              {(kind: EventTicketUrlKind | undefined) => (
                <p className="text-muted-foreground text-sm">
                  {kind === 'registration'
                    ? m.ticketLinkButtonSignup()
                    : m.ticketLinkButtonTickets()}
                </p>
              )}
            </form.Subscribe>
          ) : null}
          {field.state.meta.errors?.[0] ? (
            <p className="text-destructive text-sm">{field.state.meta.errors[0]}</p>
          ) : null}
        </div>
      )}
    </form.Field>
  )
}
